/**
 * Checkout Theme Presets
 *
 * Pre-defined themes for light and dark modes, plus utilities
 * for creating custom themes with overrides.
 */

import type { CheckoutTheme, PartialCheckoutTheme } from "./types.js";

/**
 * Light theme - Default checkout appearance
 *
 * Uses light backgrounds with dark text, matching the current checkout design.
 */
export const lightTheme: CheckoutTheme = {
  colors: {
    // Backgrounds
    background: "#f9fafb", // gray-50
    surface: "#ffffff", // white
    surfaceHover: "#f3f4f6", // gray-100
    surfaceActive: "#e5e7eb", // gray-200

    // Borders
    border: "#e5e7eb", // gray-200
    borderSubtle: "#f3f4f6", // gray-100
    borderFocus: "#3b82f6", // blue-500 (or primary)

    // Text
    text: "#111827", // gray-900
    textMuted: "#6b7280", // gray-500
    textSubtle: "#9ca3af", // gray-400

    // Primary (teal/cyan brand color from globals.css)
    primary: "#14b8a6", // teal-500
    primaryForeground: "#ffffff", // white
    primaryHover: "#0d9488", // teal-600
    primaryMuted: "#ccfbf1", // teal-100

    // Success
    success: "#dcfce7", // green-100 (background)
    successForeground: "#166534", // green-800 (text on bg)
    successSubtle: "#bbf7d0", // green-200
    successText: "#166534", // green-800
    successBorder: "#86efac", // green-300
    successMuted: "#dcfce7", // green-100

    // Error
    error: "#fee2e2", // red-100 (background)
    errorForeground: "#991b1b", // red-800 (text on bg)
    errorSubtle: "#fecaca", // red-200
    errorText: "#991b1b", // red-800
    errorBorder: "#fca5a5", // red-300
    errorMuted: "#fee2e2", // red-100

    // Warning
    warning: "#fef3c7", // amber-100 (background)
    warningForeground: "#92400e", // amber-800 (text on bg)
    warningSubtle: "#fde68a", // amber-200
    warningText: "#92400e", // amber-800
    warningBorder: "#fcd34d", // amber-300
    warningMuted: "#fef3c7", // amber-100

    // Info
    info: "#dbeafe", // blue-100 (background)
    infoForeground: "#1e40af", // blue-800 (text on bg)
    infoSubtle: "#bfdbfe", // blue-200
    infoText: "#1e40af", // blue-800
    infoBorder: "#93c5fd", // blue-300
    infoMuted: "#dbeafe", // blue-100

    // Inputs
    inputBorder: "#d1d5db", // gray-300
    inputBackground: "#ffffff", // white
    inputFocus: "#14b8a6", // teal-500 (primary)

    // Skeleton
    skeleton: "#e5e7eb", // gray-200
    skeletonHighlight: "#f3f4f6", // gray-100
  },

  typography: {
    fontFamily: "var(--font-sans, ui-sans-serif, system-ui, sans-serif)",
    fontFamilyMono: "var(--font-mono, ui-monospace, monospace)",

    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
    },

    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },

    lineHeight: {
      tight: "1.25",
      normal: "1.5",
      relaxed: "1.625",
    },
  },

  spacing: {
    radius: "0.5rem", // 8px
    radiusSm: "0.375rem", // 6px
    radiusLg: "0.75rem", // 12px
    radiusFull: "9999px", // pill
  },
};

/**
 * Dark theme - Dark mode checkout appearance
 *
 * Uses dark backgrounds with light text for dark mode interfaces.
 */
export const darkTheme: CheckoutTheme = {
  colors: {
    // Backgrounds
    background: "#111827", // gray-900
    surface: "#1f2937", // gray-800
    surfaceHover: "#374151", // gray-700
    surfaceActive: "#4b5563", // gray-600

    // Borders
    border: "#374151", // gray-700
    borderSubtle: "#1f2937", // gray-800
    borderFocus: "#60a5fa", // blue-400

    // Text
    text: "#f9fafb", // gray-50
    textMuted: "#9ca3af", // gray-400
    textSubtle: "#6b7280", // gray-500

    // Primary (lighter teal for dark mode)
    primary: "#2dd4bf", // teal-400
    primaryForeground: "#111827", // gray-900
    primaryHover: "#14b8a6", // teal-500
    primaryMuted: "#134e4a", // teal-900

    // Success
    success: "#14532d", // green-900 (background in dark)
    successForeground: "#86efac", // green-300 (text on bg)
    successSubtle: "#166534", // green-800
    successText: "#86efac", // green-300
    successBorder: "#22c55e", // green-500
    successMuted: "#14532d", // green-900

    // Error
    error: "#7f1d1d", // red-900 (background in dark)
    errorForeground: "#fca5a5", // red-300 (text on bg)
    errorSubtle: "#991b1b", // red-800
    errorText: "#fca5a5", // red-300
    errorBorder: "#ef4444", // red-500
    errorMuted: "#7f1d1d", // red-900

    // Warning
    warning: "#78350f", // amber-900 (background in dark)
    warningForeground: "#fcd34d", // amber-300 (text on bg)
    warningSubtle: "#92400e", // amber-800
    warningText: "#fcd34d", // amber-300
    warningBorder: "#f59e0b", // amber-500
    warningMuted: "#78350f", // amber-900

    // Info
    info: "#1e3a8a", // blue-900 (background in dark)
    infoForeground: "#93c5fd", // blue-300 (text on bg)
    infoSubtle: "#1e40af", // blue-800
    infoText: "#93c5fd", // blue-300
    infoBorder: "#3b82f6", // blue-500
    infoMuted: "#1e3a8a", // blue-900

    // Inputs
    inputBorder: "#4b5563", // gray-600
    inputBackground: "#1f2937", // gray-800
    inputFocus: "#2dd4bf", // teal-400

    // Skeleton
    skeleton: "#374151", // gray-700
    skeletonHighlight: "#4b5563", // gray-600
  },

  typography: {
    fontFamily: "var(--font-sans, ui-sans-serif, system-ui, sans-serif)",
    fontFamilyMono: "var(--font-mono, ui-monospace, monospace)",

    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
    },

    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },

    lineHeight: {
      tight: "1.25",
      normal: "1.5",
      relaxed: "1.625",
    },
  },

  spacing: {
    radius: "0.5rem",
    radiusSm: "0.375rem",
    radiusLg: "0.75rem",
    radiusFull: "9999px",
  },
};

/**
 * Deep merge utility for theme objects
 */
function deepMerge<T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>,
): T {
  const result = { ...target };

  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (
      sourceValue !== undefined &&
      typeof sourceValue === "object" &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      typeof targetValue === "object" &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>,
      ) as T[typeof key];
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue as T[typeof key];
    }
  }

  return result;
}

/**
 * Create a custom theme by merging overrides with a base preset
 *
 * @param basePreset - The preset theme to use as base ('light' or 'dark')
 * @param overrides - Partial theme object with custom values
 * @returns Complete theme with overrides applied
 *
 * @example
 * ```tsx
 * const customTheme = createCustomTheme('light', {
 *   colors: {
 *     primary: '#FF6B00',
 *     primaryHover: '#E55A00',
 *   },
 *   typography: {
 *     fontFamily: 'Inter, sans-serif',
 *   },
 * });
 * ```
 */
export function createCustomTheme(
  basePreset: "light" | "dark",
  overrides: PartialCheckoutTheme,
): CheckoutTheme {
  const baseTheme = basePreset === "dark" ? darkTheme : lightTheme;
  return deepMerge(baseTheme, overrides as Partial<CheckoutTheme>);
}

/**
 * Get a preset theme by name
 */
export function getPresetTheme(preset: "light" | "dark"): CheckoutTheme {
  return preset === "dark" ? darkTheme : lightTheme;
}
